import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { fileDb } from 'AppSrc/core/utility-storage/file-db';
import ReactRouterPropTypes from 'react-router-prop-types';
import { formDataShape } from 'AppSrc/core/components/forms/form-base-components/data-structure/v-f-proptypes';
import { CREATE_CRQ } from 'AppSrc/core/components/create-crq-screen/create-crq-screen-consts';
import {
  saveFormData,
  deleteAllDataForForm,
  saveFormSectionErrorsAction,
} from 'AppSrc/core/actions/actions-main-form';
import { deleteAllIndidualList } from 'AppSrc/core/approvers/approver-individual-actions';
import {
  currentSelectedNetwork,
  userRoleForSelectedNetwork,
  getIsOlaCrq,
  getIsInternationalCrq,
} from 'AppSrc/core/components/forms/form-types/create-crq/common-logic';
import {
  getEditingStatus,
} from 'AppSrc/core/components/forms/form-section-utility-functions';
import {
  COORDINATOR,
  RAISING_CRQ,
} from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import { makeGetFormData } from 'AppSrc/core/selectors/selectors-main-form';
import { FIXED } from 'AppSrc/core/components/forms/value-dependency-constants';
import { userRolesSelector } from 'AppSrc/core/auth/auth-selectors';
import { userRolesPropShape } from 'AppSrc/core/auth/auth-proptypes';
import { CHANGE_TYPE } from 'AppSrc/core/components/forms/form-types/create-crq/section-one/section-one';
import {
  selectNetworkParamsId,
} from 'AppSrc/core/components/forms/form-types/create-crq/section-one/section-one-constants';
import { submitAndUpdateCreateCrqForm } from 'AppSrc/core/actions/actions-create-crq-form';
import { SubmitCreateCrqProgress } from 'AppSrc/core/components/forms/form-types/create-crq/section-change-request-summary/submit-create-crq-progress';
import { Alert } from 'AppSrc/core/components/utility/alert';
import { getHeaderText } from 'AppSrc/core/components/forms/form-utility-functions';
import { getCreateCrqSection } from './form-types/create-crq/get-create-crq-section';
import { ExitFormButtonDisplay } from './exit-form-button';
import 'AppSrc/core/style/components/form/form.css';
import 'AppSrc/core/style/components/app-layout/page-content.css';



const getShouldAlsoSubmitToEndpoint = (params) => {
  const {
    sectionIndex,
    navigationIndex,
    userRole,
    selectedNetwork,
  } = params;

  // On fixed submit after section 4
  // On mobile after section 5
  // sections are zero indexed
  const fixedSectionFourIndex = 3;
  const mobileSectionFiveIndex = 4;
  const minSectionForSubmission = selectedNetwork === FIXED ? fixedSectionFourIndex : mobileSectionFiveIndex;

  // only submit during navigation for if the user role is COORDINATOR...
  if (userRole !== COORDINATOR) {
    return false;
  }
  // ...and you're navigating to a section after the minimum for submission...
  if (minSectionForSubmission >= navigationIndex) {
    return false;
  }

  // ...and you are navigating forwards instead of backwards
  if (sectionIndex > navigationIndex) {
    return false;
  }

  return true;
};


const formInterface = {
  [CREATE_CRQ]: getCreateCrqSection,
};

class MainForm extends PureComponent {
  constructor(props) {
    super(props);
    /*
      FN: 14/04/2020
      the navigation for the form can get quite complicated so we use state to keep references.
      Things that make the naviagtion complicated:
      1. We have to be able to navigate to backwards, forwards and straight to the summary page
      2. Whilst we can call navigateToNext and navigateToPrev directly from children components,
        Formik's handleSubmit does stuff asynchronously. As we need handleSubmit to finish before
        we navigate (and handleSubmit doesn't return a promise), we have to achieve the navigation
        through a callback. ie, Formik's handleSubmit calls this.handleSuccessfulSubmit which calls
        this.navigateToNext. this.navigateToNext then sets the target to navigate to in state before
        calling thi.snavigateToSectionFromState which handles the actual navigation.
      3. On some occasions we also have to submit to the endpoint. This is handled by
        navigateToSectionFromState calling this.props.submitFormToEndpoint(). navigateToSectionFromState
        is smart enough to know when it should submit to the endpoint.

      In other words:
        Because of the way Formik does stuff asynchronously without returning promises, we cannot
        seperate the concerns of saving a form section (through this.handleSuccessfulSubmit) from
        the concerns of navigatin to the next section. This complexity increases when middleware is
        thrown in to submit to an endpoint. Meanwhile as the back button doesn't need to go through
        Formik, it's a lot more simple.

      tl;dr:
        The navigation is a bit of a mess because it's not possible to do any of it in a particualry
        consistant way
    */


    this.state = {
      isSummaryEditing: null,
      navigationIndex: null,
    };
    this.navigateToPrev = this.navigateToPrev.bind(this);
    this.navigateToNext = this.navigateToNext.bind(this);
    this.handleSuccessfulSubmit = this.handleSuccessfulSubmit.bind(this);
    this.navigateToSectionFromState = this.navigateToSectionFromState.bind(this);
    this.navigateToSectionFromOptions = this.navigateToSectionFromOptions.bind(this);
    this.deleteAllDataForForm = this.props.deleteAllDataForForm.bind(this);
    this.onUnload = this.onUnload.bind(this);
    this.getCRQId = this.getCRQId.bind(this);
    this.getEditingStatus = this.getEditingStatus.bind(this);
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.onUnload);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onUnload);
  }

  // used to warn the user if they navigate away from the app
  onUnload(e) {
    e.preventDefault();
    e.returnValue = 'Changes that you made may not be saved.';
    return 'Changes that you made may not be saved.';
  }

  getCRQId() {
    const { formData } = this.props;
    return formData ? formData.crqId : null;
  }

  getEditingStatus() {
    const {
      formData,
      sectionIndex,
    } = this.props;
    const {
      isSummaryEditing,
    } = this.state;

    const sectionsLength = formData.formSectionsByDisplayOrder.length;

    const firstRender = sectionIndex === sectionsLength;

    return getEditingStatus({
      // editing an existing section via the back/next button
      isNavEditing: !firstRender,
      // editing on a section by section bases from the summary page
      isSummaryEditing,
      flow: RAISING_CRQ,
    });
  }

  // MM: special case when the submitted data has network type that influences routing.
  getSelectedNetworkType() {
    const {
      formType,
      formData,
    } = this.props;

    let selectedNetworkType = null;

    const {
      dataByFormSectionId,
    } = formData;

    const SELECT_NETWORK = selectNetworkParamsId;

    if ((formType === CREATE_CRQ) && dataByFormSectionId && dataByFormSectionId[CHANGE_TYPE] && dataByFormSectionId[CHANGE_TYPE].dataByFieldId[SELECT_NETWORK].value) {
      selectedNetworkType = dataByFormSectionId[CHANGE_TYPE].dataByFieldId[SELECT_NETWORK].value;
    }
    return selectedNetworkType;
  }


  navigateToPrev() {
    const {
      sectionIndex,
    } = this.props;

    const prevIndex = sectionIndex - 1;

    this.setState(() => ({
      navigationIndex: prevIndex,
    }), () => this.navigateToSectionFromState());
  }

  navigateToNext() {
    const {
      sectionIndex,
    } = this.props;

    const nextIndex = sectionIndex + 1;

    this.setState(() => ({
      navigationIndex: nextIndex,
    }), () => this.navigateToSectionFromState());
  }

  navigateToSection(destinationIndex) {
    const { formType, sectionIndex } = this.props;

    // MM: special case on section 0 when the submitted data has network type that influences routing.
    let extraRouteParam = null;

    if ((formType === CREATE_CRQ) && (sectionIndex === 0)) {
      extraRouteParam = this.getSelectedNetworkType();
    }

    this.props.navigateToSection(destinationIndex, extraRouteParam);
  }


  navigateToSectionFromState() {
    const {
      sectionIndex,
      formData,
      userRoles,
    } = this.props;

    const {
      isSummaryEditing,
      navigationIndex,
    } = this.state;

    // inline function for the navigation itself
    // this is either done when a promise resolves, or inline.
    const perfomNavigation = () => {
      if (isSummaryEditing) {
        // this only needs to be fired if returning to the summary page from work information items.
        // however we currently navigate through section indexs so that would require a refactor
        fileDb.clearAllMarked();
      }

      this.navigateToSection(navigationIndex);
      this.setState({
        navigationIndex: null,
        isSummaryEditing: null,
      });
    };

    // should we submit to the endpoint as well as navigate?
    const selectedNetwork = currentSelectedNetwork(formData);
    const userRole = userRoleForSelectedNetwork(formData, userRoles);
    // const IsOlaCrq = getIsOlaCrq(formData);
    const changeDetails = 'CHANGE_DETAILS';
    const isOloByService = formData.dataByFormSectionId[changeDetails] ? formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: Other Licensed Operator (OLO)' || formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: International Other Licensed Operator (OLO)' : null;
    const IsOlaCrq = getIsOlaCrq(formData) || isOloByService;
    const isIcmCrq = getIsInternationalCrq(formData);

    const shouldAlsoSubmitToEndpoint = getShouldAlsoSubmitToEndpoint({
      sectionIndex,
      navigationIndex,
      userRole,
      selectedNetwork,
      IsOlaCrq,
      isIcmCrq,
    });

    if (shouldAlsoSubmitToEndpoint) {
      // first submit to the endpoint
      this.props.submitFormToEndpoint()
        // then perform navigation on success
        .then(() => {
          perfomNavigation();
        });
      return;
    }
    // if we don't need to submit to api, just perform the navigation
    perfomNavigation();
  }



  navigateToSectionFromOptions(options) {
    const {
      sectionIndex,
    } = this.props;

    const {
      navToSectionIndex,
    } = options;
    this.navigateToSection(navToSectionIndex);
    this.setState({
      navigationIndex: sectionIndex,
      // FN: 21/05/2020
      // We currently only use navigateToSectionFromOptions whilst editing from summary.
      isSummaryEditing: true,
    });

    return null;
  }

  // saves the formdata to redux and then navigates to
  handleSuccessfulSubmit(sectionData, shouldAutoNav = true) {
    const editingStatus = this.getEditingStatus();

    this.props.saveFormSection(sectionData, editingStatus);

    // Sometimes we want submitting the form to automatically navigate, however sometimes we
    // want to explicitly handle this navigation ourselves
    if (shouldAutoNav) {
      // useful for when we've already set a target to navigate to, ie, the summary page
      if (this.state.navigationIndex !== null) {
        this.navigateToSectionFromState();
        return null;
      }
      // otherwise we set the target to the next page (navigateToNext also calls navigateToSectionFromState)
      this.navigateToNext();
      return null;
    }
    return null;
  }


  render() {

    const {
      formType,
      sectionIndex,
      formData,
      userRoles,
      userGroups,
      history,
      navigateToCreateCrqSectionOne,
      saveFormSectionErrors,
    } = this.props;


    const {
      isSummaryEditing,
      showSubmitConfirmation,
      submitAlertTitle,
      submitAlertText,
    } = this.state;

    const navigationProps = {
      prev: !isSummaryEditing ? this.navigateToPrev : null,
      next: !isSummaryEditing ? this.navigateToNext : null,
      cancel: isSummaryEditing ? this.navigateToSectionFromState : null,
      saveChanges: isSummaryEditing !== null, // saveChanges is bool as FormSection handles various hooks before calling handleSuccessfulSubmit
      submitForm: null, // pages that allow form submission should explicitly provide a submit form function
    };

    const CurrentSection = formInterface[formType]({
      sectionIndex,
      formData,
      userRoles,
      navigateToCreateCrqSectionOne,
    });

    const alertData = showSubmitConfirmation ? ({
      option: null,
      alertTitle: submitAlertTitle,
      alertText: submitAlertText,
      falseText: 'Close',
      trueText: 'Submit',
    }) : null;

    const selectedNetwork = currentSelectedNetwork(formData);
    // const IsOlaCrq = getIsOlaCrq(formData);
    // const IsOlaCrq = getIsOlaCrq(formData);
    const changeDetails = 'CHANGE_DETAILS';
    const isOloByService = formData.dataByFormSectionId[changeDetails] ? formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: Other Licensed Operator (OLO)' || formData.dataByFormSectionId[changeDetails].dataByFieldId.services.value === 'Network: International Other Licensed Operator (OLO)' : null;
    const IsOlaCrq = getIsOlaCrq(formData) || isOloByService;

    const crqId = this.getCRQId();
    const isIcmCrq = getIsInternationalCrq(formData);

    const headerText = getHeaderText({
      formType,
      selectedNetwork,
      crqId,
      IsOlaCrq,
      isIcmCrq,
    });

    const editingStatus = this.getEditingStatus();


    return (
      <div className="main-form-container page-content-container">

        <div className="form-top page-top">
          <h1 className="page-title">
            {headerText}
          </h1>
          <ExitFormButtonDisplay
            history={history}
            deleteAllDataForForm={this.deleteAllDataForForm}
            formId={formType}
            sectionIndex={sectionIndex}
            formData={formData}
            crqId={crqId}
          />
        </div>

        <div className="main-form page-content">

          <div className="form-fields page-content-inner">
            <CurrentSection
              sectionIndex={sectionIndex}
              handleSuccessfulSubmit={this.handleSuccessfulSubmit}
              saveFormSectionErrors={saveFormSectionErrors}
              formData={formData}
              navigateToSection={this.navigateToSectionFromOptions}
              userRoles={userRoles}
              userGroups={userGroups}
              navigationProps={navigationProps}
              editingStatus={editingStatus}
            />
          </div>
        </div>
        <SubmitCreateCrqProgress />
        {
          showSubmitConfirmation && (
            <Alert
              alertData={alertData}
              resolveAlert={this.handleConfirmation}
            />
          )
        }
      </div>
    );
  }
}

MainForm.propTypes = {
  formData: formDataShape,
  formType: PropTypes.string,
  sectionIndex: PropTypes.number,
  userRoles: userRolesPropShape,
  navigateToSection: PropTypes.func,
  saveFormSection: PropTypes.func,
  saveFormSectionErrors: PropTypes.func,
  history: ReactRouterPropTypes.history.isRequired,
  navigateToCreateCrqSectionOne: PropTypes.func,
  deleteAllDataForForm: PropTypes.func,
  submitFormToEndpoint: PropTypes.func,
  userGroups: PropTypes.objectOf(PropTypes.array),
};


// MM: this is so each instance of the container gets its own mapStateToProps
// otherwise the selectors that depend on props don't properly memoize
// https://redux.js.org/recipes/computing-derived-data#sharing-selectors-across-multiple-components
const makeMapStateToProps = () => {
  const getFormData = makeGetFormData();
  const mapStateToProps = (state, ownProps) => ({
    userRoles: userRolesSelector(state),
    formData: getFormData(state, ownProps),
    userGroups: state && state.auth.user.groups,
  });
  return mapStateToProps;
};

// mapDispatchToProps tells the container component how to dispatch actions to the redux store
const mapDispatchToProps = (dispatch, ownProps) => ({
  saveFormSectionErrors: (sectionId, errors) => {
    dispatch(saveFormSectionErrorsAction({
      formId: ownProps.formType,
      sectionId,
      errors,
    }));
  },
  saveFormSection: (sectionData, editingStatus) => {
    dispatch(saveFormData({
      formId: ownProps.formType,
      sectionData,
      editingStatus,
    }));
  },
  deleteAllDataForForm: (formId) => {
    dispatch(deleteAllDataForForm(formId));
    // To delete individual approver list
    dispatch(deleteAllIndidualList(formId));
  },
  submitFormToEndpoint() {
    // optional param of crqId for update as opposed to create
    return dispatch(submitAndUpdateCreateCrqForm())
      .catch((error) => {
        console.error(error);
        return Promise.reject(error);
      });
  },
});

MainForm = connect(
  makeMapStateToProps,
  mapDispatchToProps,
)(MainForm);


export {
  MainForm,
};


